import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, EmptyState } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";
const SERVICES = ["Listings", "Presence", "Social", "Production", "Website"];

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: "", industry: "", account_manager: "", contact_email: "", contact_phone: "", services: [], status: "Active", notes: "" });

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async () => {
    if (!form.brand) return toast.error("Brand name is required");
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setOpen(false);
      setForm({ brand: "", industry: "", account_manager: "", contact_email: "", contact_phone: "", services: [], status: "Active", notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/clients/${id}`);
    toast.success("Client removed");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Client / Brand Master"
        subtitle={`${clients.length} brands under management`}
        actions={
          <button data-testid="add-client-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Client
          </button>
        }
      />
      <div className="p-8">
        {clients.length === 0 ? (
          <EmptyState icon={Building2} title="No clients yet" hint="Add your first brand to begin." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((c) => (
              <div key={c.id} data-testid={`client-card-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)} className="bg-[#121212] border border-white/10 rounded-sm p-5 hover:border-white/20 transition-colors group cursor-pointer">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-medium">{c.brand}</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">{c.industry || "—"}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(c.services || []).map((s) => (
                    <span key={s} className="text-[10px] font-mono uppercase tracking-wider border border-white/10 bg-white/[0.03] text-zinc-400 px-2 py-0.5 rounded-sm">{s}</span>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs text-zinc-500">AM: {c.account_manager || "Unassigned"}</span>
                  <button data-testid={`delete-client-${c.id}`} onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Client / Brand</DialogTitle><DialogDescription className="sr-only">Add a new client brand to the master directory.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <input data-testid="client-brand-input" className={inputCls} placeholder="Brand name *" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Industry" value={form.industry} onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} />
              <input className={inputCls} placeholder="Account manager" value={form.account_manager} onChange={(e) => setForm((f) => ({ ...f, account_manager: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} />
              <input className={inputCls} placeholder="Contact phone" value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-2 block font-medium">Services</label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button key={s} data-testid={`service-toggle-${s}`} onClick={() => toggleService(s)} className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${form.services.includes(s) ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:bg-white/5"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="client-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
              Add Client
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
